import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  Bell,
  Check,
  ClipboardList,
  Clock3,
  Info,
  RefreshCcw,
  RotateCcw,
  Trash2,
  User,
  X,
} from "lucide-react";
import {
  formatAlertAbsoluteDate,
  formatAlertSince,
  getAlertDisplayCopy,
  getAlertReferenceDate,
  isClubLevelAlert,
  resolveAlertPlayerAvatar,
} from "./alertPresentationUtils";
import InjurySummaryPanel from "../players/InjurySummaryPanel";

const FILTERS = [
  { key: "all", labelKey: "alerts_center.filter_all", defaultLabel: "All" },
  { key: "unread", labelKey: "alerts_center.filter_unread", defaultLabel: "Unread" },
  { key: "read", labelKey: "alerts_center.filter_read", defaultLabel: "Read" },
];

function severityTone(severity, isDarkMode) {
  const key = String(severity || "medium").toLowerCase();

  if (key === "high" || key === "critical") {
    return isDarkMode
      ? "bg-red-500/20 text-red-300 border-red-500/40"
      : "bg-red-50 text-red-700 border-red-300";
  }

  if (key === "low") {
    return isDarkMode
      ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
      : "bg-emerald-50 text-emerald-700 border-emerald-300";
  }

  return isDarkMode
    ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
    : "bg-amber-50 text-amber-700 border-amber-300";
}

function typeLabel(alertType, translated) {
  if (translated) return translated;
  const value = String(alertType || "alert");
  return value
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function getPlayerInitials(label) {
  const tokens = String(label || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!tokens.length) return "--";
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return `${tokens[0][0]}${tokens[1][0]}`.toUpperCase();
}

export default function AlertsCenterModal({
  isOpen,
  onClose,
  alerts,
  loading,
  error,
  filter,
  onFilterChange,
  unreadCount,
  isDarkMode,
  onRefresh,
  onOpenAlert,
  onMarkRead,
  onMarkUnread,
  onDeleteAlert,
}) {
  const { t, i18n } = useTranslation();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/45 px-3 py-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className={`flex h-[82vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border shadow-2xl ${
              isDarkMode ? "border-gray-700 bg-gray-900 text-gray-100" : "border-gray-200 bg-white text-gray-900"
            }`}
            initial={{ opacity: 0, y: 14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between border-b px-5 py-4 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
              <div className="flex items-center gap-3">
                <div className={`rounded-lg p-2 ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}>
                  <Bell size={18} />
                </div>
                <div>
                  <h2 className="text-base font-semibold">{t("alerts_center.title", { defaultValue: "Alerts" })}</h2>
                  <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {unreadCount} {t("alerts_center.unread", { defaultValue: "unread" })}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onRefresh}
                  className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                    isDarkMode
                      ? "border-gray-600 text-gray-200 hover:bg-gray-800"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <RefreshCcw size={13} />
                  {t("alerts_center.refresh", { defaultValue: "Refresh" })}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className={`rounded-md p-1.5 transition-colors ${
                    isDarkMode ? "text-gray-300 hover:bg-gray-800" : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className={`flex flex-wrap gap-2 border-b px-5 py-3 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
              {FILTERS.map((item) => {
                const active = filter === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => onFilterChange(item.key)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      active
                        ? isDarkMode
                          ? "border-indigo-400 bg-indigo-500/20 text-indigo-200"
                          : "border-indigo-300 bg-indigo-50 text-indigo-700"
                        : isDarkMode
                          ? "border-gray-600 text-gray-300 hover:bg-gray-800"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {t(item.labelKey, { defaultValue: item.defaultLabel })}
                  </button>
                );
              })}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {loading && (
                <div className="flex h-full items-center justify-center">
                  <div className="flex items-center gap-2 text-sm opacity-80">
                    <RefreshCcw size={15} className="animate-spin" />
                    {t("alerts_center.loading", { defaultValue: "Loading alerts..." })}
                  </div>
                </div>
              )}

              {!loading && error && (
                <div className={`rounded-xl border p-4 ${isDarkMode ? "border-red-500/40 bg-red-500/10" : "border-red-200 bg-red-50"}`}>
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={16} className="mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold">{t("alerts_center.load_error", { defaultValue: "Unable to load alerts" })}</p>
                      <p className="mt-1 text-xs opacity-80">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {!loading && !error && alerts.length === 0 && (
                <div className={`flex h-full flex-col items-center justify-center rounded-2xl border ${isDarkMode ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-gray-50"}`}>
                  <Bell size={22} className={isDarkMode ? "text-gray-400" : "text-gray-500"} />
                  <p className="mt-3 text-sm font-semibold">{t("alerts_center.empty_title", { defaultValue: "No alerts" })}</p>
                  <p className={`mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {t("alerts_center.empty_description", { defaultValue: "Validated alerts will appear here." })}
                  </p>
                </div>
              )}

              {!loading && !error && alerts.length > 0 && (
                <div className="space-y-3">
                  {alerts.map((alert) => {
                    const isRead = !!alert?.coach_state?.is_read;
                    const tone = severityTone(alert?.severity, isDarkMode);
                    const copy = getAlertDisplayCopy(alert, t);
                    const clubLevel = isClubLevelAlert(alert);
                    const subjectLabel = copy.subjectLabel;
                    const avatarSrc = clubLevel ? null : resolveAlertPlayerAvatar(alert);
                    const detectedDate = getAlertReferenceDate(alert);
                    const since = formatAlertSince(detectedDate, i18n?.language) || "-";
                    const detectedAt = formatAlertAbsoluteDate(detectedDate) || "-";
                    const severityLabel = String(alert?.severity || "medium").toUpperCase();
                    const activeInjuries = clubLevel
                      ? []
                      : (Array.isArray(alert?.player?.active_injuries)
                        ? alert.player.active_injuries
                        : []);

                    return (
                      <button
                        key={alert.id}
                        type="button"
                        onClick={() => onOpenAlert(alert)}
                        className={`w-full rounded-xl border p-4 text-left transition-all ${
                          isRead
                            ? isDarkMode
                              ? "border-gray-700 bg-gray-800/40 hover:bg-gray-800"
                              : "border-gray-200 bg-white hover:bg-gray-50"
                            : isDarkMode
                              ? "border-indigo-500/50 bg-indigo-500/10 hover:bg-indigo-500/20"
                              : "border-indigo-300 bg-indigo-50/70 hover:bg-indigo-50"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div
                            className={`flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border ${
                              isDarkMode ? "border-gray-600 bg-gray-800 text-gray-200" : "border-gray-300 bg-gray-100 text-gray-700"
                            }`}
                          >
                            {avatarSrc ? (
                              <img
                                src={avatarSrc}
                                alt={subjectLabel}
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            ) : clubLevel ? (
                              <ClipboardList size={18} />
                            ) : (
                              <span className="text-xs font-bold tracking-wide">{getPlayerInitials(subjectLabel)}</span>
                            )}
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span
                                className={`inline-flex max-w-full items-center gap-1.5 rounded-lg border px-2.5 py-1 text-sm font-bold tracking-tight ${
                                  isDarkMode
                                    ? "border-indigo-500/40 bg-indigo-500/15 text-indigo-100"
                                    : "border-indigo-200 bg-indigo-50 text-indigo-900"
                                }`}
                              >
                                <User size={14} className="shrink-0 opacity-80" />
                                <span className="truncate">{subjectLabel}</span>
                              </span>
                              <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
                                {severityLabel}
                              </span>
                              {!isRead ? (
                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                                  isDarkMode ? "bg-indigo-500/20 text-indigo-200" : "bg-indigo-100 text-indigo-700"
                                }`}>
                                  {t("player_risk_alert.unread")}
                                </span>
                              ) : null}
                            </div>

                            <div className="mt-2 flex items-center gap-2">
                              {!isRead ? <span className="h-2 w-2 shrink-0 rounded-full bg-indigo-500" /> : <Info size={12} className="shrink-0 opacity-60" />}
                              <p className="text-sm font-semibold">{copy.title}</p>
                            </div>

                            <p className={`mt-1 inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                              <span className="inline-flex items-center gap-1">
                                <Clock3 size={12} />
                                {t("player_risk_alert.since", { defaultValue: "Since" })} {since}
                              </span>
                              <span>{t("player_risk_alert.detected", { defaultValue: "Detected" })}: {detectedAt}</span>
                            </p>

                            <p className={`mt-1 text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                              {copy.description}
                            </p>

                            {activeInjuries.length > 0 ? (
                              <InjurySummaryPanel injuries={activeInjuries} isDarkMode={isDarkMode} />
                            ) : null}

                            <div className={`mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                              <span className="inline-flex items-center gap-1">
                                <AlertTriangle size={12} />
                                {typeLabel(alert.alert_type, copy.typeLabel)}
                              </span>
                            </div>
                          </div>

                          <div className="flex shrink-0 items-center gap-2">
                            {isRead ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onMarkUnread(alert.id);
                                }}
                                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium ${
                                  isDarkMode
                                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                                }`}
                              >
                                <RotateCcw size={12} />
                                {t("alerts_center.mark_unread", { defaultValue: "Unread" })}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onMarkRead(alert.id);
                                }}
                                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium ${
                                  isDarkMode
                                    ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20"
                                    : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                }`}
                              >
                                <Check size={12} />
                                {t("alerts_center.mark_read", { defaultValue: "Mark read" })}
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteAlert(alert.id);
                              }}
                              className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium ${
                                isDarkMode
                                  ? "border-red-500/40 text-red-300 hover:bg-red-500/20"
                                  : "border-red-300 text-red-700 hover:bg-red-50"
                              }`}
                            >
                              <Trash2 size={12} />
                              {t("alerts_center.delete", { defaultValue: "Delete" })}
                            </button>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
